/**
 * Lab Test Harness Dashboard
 * Admin UI for controlling the lab integration test harness
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon,
  Download as DownloadIcon,
  NetworkCheck as NetworkCheckIcon,
  AutoFixHigh as AutoFillIcon
} from '@mui/icons-material';
import testHarnessService from '../../services/testHarnessService';

// Example HL7 order message for auto-fill
const EXAMPLE_HL7_ORDER = `MSH|^~\\&|EMR|FACILITY|LAB|LAB_FACILITY|20240101120000||ORM^O01|MSG123|P|2.5.1
PID|1|||PAT001^^^FACILITY||Patient^Test
ORC|NW|ORD001
OBR|1|ORD001||2345-7^Glucose^LN`;

// Example FHIR ServiceRequest for auto-fill
const EXAMPLE_FHIR_ORDER = JSON.stringify({
  resourceType: "ServiceRequest",
  id: "example-lab-order",
  status: "active",
  intent: "order",
  code: {
    coding: [{
      system: "http://loinc.org",
      code: "2345-7",
      display: "Glucose"
    }]
  },
  subject: {
    reference: "Patient/PAT001",
    display: "Test Patient"
  },
  authoredOn: new Date().toISOString()
}, null, 2);

// Tab panel component
function TabPanel({ children, value, index, ...props }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...props}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Status chip component
function StatusChip({ status }) {
  const statusConfig = {
    running: { color: 'success', icon: <PlayIcon fontSize="small" /> },
    stopped: { color: 'default', icon: <StopIcon fontSize="small" /> },
    passed: { color: 'success', icon: <CheckIcon fontSize="small" /> },
    failed: { color: 'error', icon: <CloseIcon fontSize="small" /> },
    error: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
    pending: { color: 'warning', icon: <WarningIcon fontSize="small" /> },
    completed: { color: 'success', icon: <CheckIcon fontSize="small" /> },
    processing: { color: 'info', icon: <CircularProgress size={14} /> }
  };

  const config = statusConfig[status] || { color: 'default', icon: null };

  return (
    <Chip
      label={status}
      color={config.color}
      size="small"
      icon={config.icon}
    />
  );
}

export default function LabTestHarness() {
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Simulator state
  const [simulatorStatus, setSimulatorStatus] = useState(null);
  const [sftpConfig, setSftpConfig] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [logs, setLogs] = useState([]);
  const [testRuns, setTestRuns] = useState([]);
  const [stats, setStats] = useState(null);

  // Form state
  const [selectedScenario, setSelectedScenario] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('HL7');
  const [orderId, setOrderId] = useState('');
  const [orderContent, setOrderContent] = useState('');

  // Dialog state
  const [resultDialog, setResultDialog] = useState({ open: false, result: null });

  // Load all data function
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, scenariosRes, statsRes, configRes] = await Promise.all([
        testHarnessService.getSimulatorStatus(),
        testHarnessService.getScenarios(),
        testHarnessService.getStats(),
        testHarnessService.getConfig()
      ]);

      if (statusRes.success) setSimulatorStatus(statusRes.data);
      if (configRes.success) setSftpConfig(configRes.data);
      if (scenariosRes.success) {
        const loadedScenarios = scenariosRes.data.scenarios || [];
        setScenarios(loadedScenarios);
        if (loadedScenarios.length > 0) {
          setSelectedScenario(prev => prev || loadedScenarios[0]?.type || '');
        }
      }
      if (statsRes.success) setStats(statsRes.data);

      const [logsRes, runsRes] = await Promise.all([
        testHarnessService.getLogs({ limit: 20 }),
        testHarnessService.getTestRuns({ limit: 10 })
      ]);

      if (logsRes.success) setLogs(logsRes.data.logs || []);
      if (runsRes.success) setTestRuns(runsRes.data.runs || []);

      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Simulator controls
  const handleStartSimulator = async () => {
    setLoading(true);
    try {
      const result = await testHarnessService.startSimulator();
      if (result.success) {
        setSuccess('Simulator started');
        setSimulatorStatus(result.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStopSimulator = async () => {
    setLoading(true);
    try {
      const result = await testHarnessService.stopSimulator();
      if (result.success) {
        setSuccess('Simulator stopped');
        setSimulatorStatus(result.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSimulator = async () => {
    setLoading(true);
    try {
      const result = await testHarnessService.resetSimulator(true);
      if (result.success) {
        setSuccess('Simulator reset');
        await loadAllData();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    setLoading(true);
    try {
      const result = await testHarnessService.testConnection();
      if (result.success) {
        setSuccess(`Connection successful: ${result.message}`);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Run scenario
  const handleRunScenario = async () => {
    if (!selectedScenario) {
      setError('Please select a scenario');
      return;
    }

    setLoading(true);
    try {
      const result = await testHarnessService.runScenario(
        selectedScenario,
        selectedFormat,
        orderId ? { orderId } : null
      );

      if (result.success) {
        setSuccess('Scenario executed successfully');
        setResultDialog({ open: true, result: result.data.result });
        await loadAllData();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Process custom order
  const handleProcessOrder = async () => {
    if (!orderContent.trim()) {
      setError('Please enter order content');
      return;
    }

    setLoading(true);
    try {
      const result = await testHarnessService.processOrder(
        orderContent,
        selectedScenario,
        selectedFormat
      );

      if (result.success) {
        setSuccess('Order processed successfully');
        setResultDialog({ open: true, result: result.data });
        setOrderContent('');
        await loadAllData();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Poll for orders
  const handlePollOrders = async () => {
    setLoading(true);
    try {
      const result = await testHarnessService.pollOrders();
      if (result.success) {
        setSuccess(`Processed ${result.data.processedCount} orders`);
        await loadAllData();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ScienceIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">Lab Integration Test Harness</Typography>
            <Typography variant="body2" color="text.secondary">
              Test and validate lab integration workflows
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadAllData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Loading indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Simulator Status</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <StatusChip status={simulatorStatus?.running ? 'running' : 'stopped'} />
              </Box>
            </CardContent>
            <CardActions>
              {simulatorStatus?.running ? (
                <Button size="small" color="error" startIcon={<StopIcon />} onClick={handleStopSimulator}>
                  Stop
                </Button>
              ) : (
                <Button size="small" color="success" startIcon={<PlayIcon />} onClick={handleStartSimulator}>
                  Start
                </Button>
              )}
              <Button size="small" onClick={handleResetSimulator}>Reset</Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Orders Processed</Typography>
              <Typography variant="h4">{stats?.simulator?.ordersProcessed || 0}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip label={`${stats?.simulator?.resultsGenerated || 0} results`} size="small" color="success" variant="outlined" />
                <Chip label={`${stats?.simulator?.errors || 0} errors`} size="small" color="error" variant="outlined" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">SFTP Connection</Typography>
              {sftpConfig?.configured ? (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {sftpConfig.sftp?.host || 'N/A'}:{sftpConfig.sftp?.port || 22}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    User: {sftpConfig.sftp?.username || 'N/A'}
                  </Typography>
                  <Chip 
                    label={sftpConfig.sftp?.hasPrivateKey ? 'Key Auth' : 'Password'} 
                    size="small" 
                    color={sftpConfig.sftp?.hasPrivateKey ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Not configured
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<NetworkCheckIcon />} onClick={handleTestConnection}>
                Test Connection
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Quick Actions</Typography>
            </CardContent>
            <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
              <Button size="small" startIcon={<RefreshIcon />} onClick={handlePollOrders} fullWidth>
                Poll for Orders
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Run Tests" icon={<PlayIcon />} iconPosition="start" />
          <Tab label="Process Order" icon={<SendIcon />} iconPosition="start" />
          <Tab label="Activity Logs" />
        </Tabs>
      </Paper>

      {/* Run Tests Tab */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Run Test Scenario</Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Scenario</InputLabel>
                <Select
                  value={selectedScenario}
                  label="Scenario"
                  onChange={(e) => setSelectedScenario(e.target.value)}
                >
                  {scenarios.map((s) => (
                    <MenuItem key={s.type} value={s.type}>
                      <Box>
                        <Typography variant="body2">{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Output Format</InputLabel>
                <Select
                  value={selectedFormat}
                  label="Output Format"
                  onChange={(e) => setSelectedFormat(e.target.value)}
                >
                  <MenuItem value="HL7">HL7 v2.x</MenuItem>
                  <MenuItem value="FHIR">FHIR R4</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Order ID (optional)"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Leave blank to auto-generate"
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={handleRunScenario}
                disabled={loading}
                fullWidth
              >
                Run Scenario
              </Button>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Available Scenarios</Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {scenarios.map((scenario) => (
                  <Accordion key={scenario.type} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2">{scenario.name}</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip
                          label={scenario.type.includes('IMAGING') ? 'Imaging' : 'Lab'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        {scenario.description}
                      </Typography>
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => {
                          setSelectedScenario(scenario.type);
                          setActiveTab(0);
                        }}
                      >
                        Select
                      </Button>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Process Order Tab */}
      <TabPanel value={activeTab} index={1}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Process Custom Order</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste an HL7 ORM/OML message or FHIR ServiceRequest JSON to process
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={10}
            label="Order Content"
            value={orderContent}
            onChange={(e) => setOrderContent(e.target.value)}
            placeholder={selectedFormat === 'FHIR' ? 'Paste FHIR ServiceRequest JSON here or click "Use Example"' : 'Paste HL7 ORM/OML message here or click "Use Example"'}
            sx={{ mb: 2 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Response Scenario</InputLabel>
                <Select
                  value={selectedScenario}
                  label="Response Scenario"
                  onChange={(e) => setSelectedScenario(e.target.value)}
                >
                  {scenarios.map((s) => (
                    <MenuItem key={s.type} value={s.type}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Response Format</InputLabel>
                <Select
                  value={selectedFormat}
                  label="Response Format"
                  onChange={(e) => setSelectedFormat(e.target.value)}
                >
                  <MenuItem value="HL7">HL7 v2.x</MenuItem>
                  <MenuItem value="FHIR">FHIR R4</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleProcessOrder}
              disabled={loading || !orderContent.trim()}
            >
              Process Order
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoFillIcon />}
              onClick={() => setOrderContent(selectedFormat === 'FHIR' ? EXAMPLE_FHIR_ORDER : EXAMPLE_HL7_ORDER)}
            >
              Use Example
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setOrderContent('')}
              disabled={!orderContent}
            >
              Clear
            </Button>
          </Box>
        </Paper>
      </TabPanel>

      {/* Activity Logs Tab */}
      <TabPanel value={activeTab} index={2}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    {log.timestamp?._seconds
                      ? new Date(log.timestamp._seconds * 1000).toLocaleString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip label={log.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {JSON.stringify(log.data)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No activity logs yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Result Dialog */}
      <Dialog
        open={resultDialog.open}
        onClose={() => setResultDialog({ open: false, result: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generated Result</DialogTitle>
        <DialogContent>
          {resultDialog.result && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Filename: {resultDialog.result.filename}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Generated: {resultDialog.result.generatedAt ? new Date(resultDialog.result.generatedAt).toLocaleString() : '-'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Content:</Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 400,
                  overflow: 'auto',
                  backgroundColor: 'grey.50',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {resultDialog.result.content}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const blob = new Blob([resultDialog.result?.content || ''], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = resultDialog.result?.filename || 'result.txt';
              a.click();
              URL.revokeObjectURL(url);
            }}
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
          <Button onClick={() => setResultDialog({ open: false, result: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

